import { useEffect, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { BackBtn, CheckBox, NVButton, Select, TextField } from '@nv/basic-components'
import { NVBasicModal, useNVModal } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { AdminVoterAuthService, AdminVoterService } from '@nv/services'
import { days, months, years } from '@nv/utils'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { AddressInput } from '../registration/address/AddressInput'
import { MailingAddressInput } from '../registration/address/MailingAddressInput'
import styles from './AdminEditElectorRegistration.module.scss'

export const AdminEditElectorRegistration = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { voterId } = useParams()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const { NVModal, openModal } = useNVModal()
  const { electionDate, minAge, includeSingleNameField, knownIdNumberTextFieldConfig, includeKnownIdNumber } = useConfigurationContext()

  useEffect(() => {
    const getUser = async () => {
      try {
        setLoading(true)
        const resp = await AdminVoterAuthService.getVoterDetails({ voterId: voterId })
        if (resp) {
          setUser(resp)
        }
      } catch (error) {
        console.error('Failed to fetch voter details:', error)
      } finally {
        setLoading(false)
      }
    }
    getUser()
  }, [])

  const validationSchema = Yup.object()
    .shape({
      firstName: Yup.string().required(t('admin.users.firstNameRequired')),
      lastName: Yup.string().when('singleName', {
        is: false,
        then: Yup.string().required(t('admin.users.lastNameRequired')),
        otherwise: Yup.string(),
      }),
      dobMonth: Yup.string().required(t('admin.users.monthRequired')),
      dobDay: Yup.string().required(t('admin.users.dayRequired')),
      dobYear: Yup.string().required(t('admin.users.yearRequired')),
      singleName: Yup.boolean(),
      addressNotListed: Yup.boolean(),
      searchAddress: Yup.object().when('addressNotListed', {
        is: false,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.address')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      address: Yup.object().when('addressNotListed', {
        is: true,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.street')),
            unitNumberPOBox: Yup.string().notRequired(),
            city: Yup.string().required(t('form.validations.city')),
            province: Yup.string().required(t('form.validations.province')),
            postalCode: Yup.string().required(t('form.validations.postalCode')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      mailingAddressNotListed: Yup.boolean(),
      searchMailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed'], {
        is: (differentMailingAddress, mailingAddressNotList) => differentMailingAddress && !mailingAddressNotList,
        then: () =>
          Yup.object().shape({
            formattedAddress: Yup.string().required(t('form.validations.address')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      mailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed'], {
        is: (differentMailingAddress, mailingAddressNotList) => differentMailingAddress && mailingAddressNotList,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.street')),
            unitNumberPOBox: Yup.string().notRequired(),
            city: Yup.string().required(t('form.validations.city')),
            province: Yup.string().required(t('form.validations.province')),
            postalCode: Yup.string().required(t('form.validations.postalCode')),
            country: Yup.string().required(t('form.validations.country')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      knownIdNumber: Yup.string().notRequired(),
    })
    .test('is-valid-date', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const date = new Date(year, month - 1, day)
      const isValidDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
      return isValidDate || this.createError({ path: 'dobYear', message: t('registration.form.validations.validBirthDate') })
    })
    .test('is-over-18', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const dateCompareBirthday = electionDate ? electionDate : new Date()
      const birthDate = new Date(year, month - 1, day)
      const age = dateCompareBirthday.getFullYear() - birthDate.getFullYear()
      const monthDiff = dateCompareBirthday.getMonth() - birthDate.getMonth()
      const dayDiff = dateCompareBirthday.getDate() - birthDate.getDate()
      const isOverMinAge = age > minAge || (age === minAge && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
      return isOverMinAge || this.createError({ path: 'dobYear', message: t('registration.form.validations.minAge', { age: minAge }) })
    })

  const formik = useFormik({
    initialValues: {
      firstName: user?.voterInfo?.firstName || '',
      middleName: user?.voterInfo?.middleName || '',
      lastName: user?.voterInfo?.lastName || '',
      singleName: user?.voterInfo?.singleName || false,
      dobMonth: user?.voterInfo?.monthBorn ? Number(user.voterInfo.monthBorn) : '',
      dobDay: user?.voterInfo?.dayBorn ? Number(user.voterInfo.dayBorn) : '',
      dobYear: user?.voterInfo?.yearBorn ? Number(user.voterInfo.yearBorn) : '',
      knownIdNumber: user?.voterInfo?.knownIdNumber || '',
      addressNotListed: !user?.voterInfo?.address?.district,
      address: {
        streetAddress: user?.voterInfo?.address?.streetAddress || '',
        unitNumberPOBox: user?.voterInfo?.address?.unitNumberPOBox || '',
        city: user?.voterInfo?.address?.city || '',
        province: user?.voterInfo?.address?.province || '',
        postalCode: user?.voterInfo?.address?.postalCode || '',
        district: user?.voterInfo?.address?.district || '',
        districts: user?.voterInfo?.address?.districts || [],
      },
      searchAddress: {
        streetAddress: user?.voterInfo?.address?.streetAddress || '',
        unitNumberPOBox: user?.voterInfo?.address?.unitNumberPOBox || '',
        city: user?.voterInfo?.address?.city || '',
        province: user?.voterInfo?.address?.province || '',
        postalCode: user?.voterInfo?.address?.postalCode || '',
        _id: user?.voterInfo?.address?._id || '',
        district: user?.voterInfo?.address?.district || '',
        districts: user?.voterInfo?.address?.districts || [],
        formattedAddress: user?.voterInfo?.address?.fullAddressNoUnit || '',
        value: user ? { ...user.voterInfo.address, label: user?.voterInfo?.address?.streetAddress } : '',
        label: user?.voterInfo?.address?.fullAddressNoUnit || '',
      },
      mailingAddressNotListed: false,
      mailingAddress: {
        streetAddress: user?.voterInfo?.mailingAddress?.streetAddress || '',
        unitNumberPOBox: user?.voterInfo?.mailingAddress?.unitNumberPOBox || '',
        city: user?.voterInfo?.mailingAddress?.city || '',
        province: user?.voterInfo?.mailingAddress?.province || '',
        postalCode: user?.voterInfo?.mailingAddress?.postalCode || '',
        country: user?.voterInfo?.mailingAddress?.country || '',
      },
      searchMailingAddress: {
        streetAddress: user?.voterInfo?.mailingAddress?.streetAddress || '',
        unitNumberPOBox: user?.voterInfo?.mailingAddress?.unitNumberPOBox || '',
        city: user?.voterInfo?.mailingAddress?.city || '',
        province: user?.voterInfo?.mailingAddress?.province || '',
        postalCode: user?.voterInfo?.mailingAddress?.postalCode || '',
        country: user?.voterInfo?.mailingAddress?.country || '',
        formattedAddress: user?.voterInfo?.mailingAddress?.fullAddressNoUnit || '',
        value: user ? { ...user.voterInfo?.mailingAddress, label: user?.voterInfo?.mailingAddress?.fullAddressNoUnit } : '',
        label: user?.voterInfo?.mailingAddress?.fullAddressNoUnit || '',
      },
      differentMailingAddress: user?.voterInfo?.mailingAddress?.streetAddress ? true : false,
      email: user?.email,
      confirmEmail: user?.email,
      phone: user?.phone,
      VSD: user?.voterInfo?.address?.districts?.find((a) => a.wardType === 'VSD')?.ward || '',
      CBE: user?.voterInfo?.address?.districts?.find((a) => a.wardType === 'CBE')?.ward ? 'YES' : '',
      CCSD: user?.voterInfo?.address?.districts?.find((a) => a.wardType === 'CCSD')?.ward ? 'YES' : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const userData = {
        ...values,
        dateOfBirth: `${values.dobYear}-${values.dobMonth.toString().padStart(2, '0')}-${values.dobDay.toString().padStart(2, '0')}`,
        address: values.addressNotListed ? values.address : values.searchAddress,
      }
      if (values.differentMailingAddress) {
        userData.mailingAddress = values.mailingAddressNotListed ? values.mailingAddress : values.searchMailingAddress
      }

      setLoading(true)
      const res = await AdminVoterService.updateRegistration(voterId, userData)
      if (res.success) {
        openModal()
      } else {
        // console.log(res.error)
      }

      setLoading(false)
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    if (formik.values.addressNotListed) {
      formik.setFieldValue('VSD', '')
      formik.setFieldValue('CBE', '')
      formik.setFieldValue('CCSD', '')
    } else {
      formik.setFieldValue('VSD', formik.values.searchAddress?.districts?.find((a) => a.wardType === 'VSD')?.ward || '')
      formik.setFieldValue('CBE', formik.values.searchAddress?.districts?.find((a) => a.wardType === 'CBE')?.ward ? 'YES' : '')
      formik.setFieldValue('CCSD', formik.values.searchAddress?.districts?.find((a) => a.wardType === 'CCSD')?.ward ? 'YES' : '')
    }
  }, [formik.values.searchAddress?.streetAddress, formik.values.addressNotListed])
  return (
    <>
      <NVModal>
        <NVBasicModal
          title={t('admin.electorsList.saveElector')}
          text={t('admin.electorsList.successfullySavedElector')}
          success={true}
          onClickOk={() => navigate(-1)}
        />
      </NVModal>

      {!loading && (
        <div className={styles['container']}>
          <BackBtn />
          <div className={styles['container__form']}>
            <h1>{t('admin.users.editElector')}</h1>

            <h2 className={styles['hr']}>{t('registration.basicInfo')}</h2>
            <form className={styles['form']}>
              {/* Name Section */}
              <div className={styles['form__name']}>
                <TextField
                  formik={formik}
                  type='text'
                  field='firstName'
                  placeholder={t('form.labels.firstName')}
                  label={t('form.labels.firstName')}
                  isRequired={true}
                  autocomplete='off'
                />
                <TextField
                  formik={formik}
                  type='text'
                  field='middleName'
                  placeholder={t('form.labels.middleName')}
                  label={t('form.labels.middleName')}
                  isRequired={false}
                  autocomplete='off'
                />
                <TextField
                  formik={formik}
                  type='text'
                  field='lastName'
                  placeholder={t('form.labels.lastName')}
                  label={t('form.labels.lastName')}
                  isRequired={!formik.values.singleName}
                  autocomplete='off'
                />
              </div>

              {includeSingleNameField && (
                <CheckBox
                  formik={formik}
                  field='singleName'
                  checked={formik.values.singleName}
                  onChange={() => formik.setFieldValue('singleName', !formik.values.singleName)}
                  label={t('registration.singleName')}
                  square={true}
                />
              )}

              {/* Date of Birth */}
              <div className={styles['form__birth']}>
                <div className={styles['form__select-container']}>
                  <div className={styles['form__item-title']}>
                    {t('form.labels.dateOfBirth')} <span className={styles['form__required']}>*</span>
                  </div>
                  <div className={styles['form__selects']}>
                    <Select
                      label={t('countdown.time.calendar.month')}
                      formik={formik}
                      options={months()}
                      field='dobMonth'
                      value={formik.values.dobMonth}
                      isRequired={true}
                      ariaLabel={t('registration.dateOfBirthMonth')}
                    />
                    <Select
                      label={t('countdown.time.calendar.day')}
                      formik={formik}
                      options={days}
                      field='dobDay'
                      value={formik.values.dobDay}
                      isRequired={true}
                      ariaLabel={t('registration.dateOfBirthDay')}
                    />
                    <Select
                      label={t('countdown.time.calendar.year')}
                      formik={formik}
                      options={years}
                      field='dobYear'
                      value={formik.values.dobYear}
                      isRequired={true}
                      ariaLabel={t('registration.dateOfBirthYear')}
                    />
                  </div>
                </div>
              </div>

              <h2 className={styles['hr']}>{t('registration.qualifyingResidentialAddress')}</h2>
              <AddressInput formik={formik} styles={styles} />
              {KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isMonitoringAdmin() ? (
                <div className={styles['form__name']}>
                  <TextField
                    formik={formik}
                    type='text'
                    field='VSD'
                    placeholder={t('VSD')} // missing translation
                    label={t('VSD')} // missing translation
                    isRequired={true}
                    autocomplete='off'
                    //@ts-ignore
                    disabled={!formik.values.addressNotListed && formik.values.searchAddress?.districts?.find((a) => a.wardType === 'VSD')}
                  />
                  <TextField
                    formik={formik}
                    type='text'
                    field='CBE'
                    label={'CBE (type YES for eligible or leave blank if not)'} // missing translation
                    autocomplete='off'
                    //@ts-ignore
                    disabled={!formik.values.addressNotListed && formik.values.searchAddress?.districts?.find((a) => a.wardType === 'VSD')}
                  />
                  <TextField
                    formik={formik}
                    type='text'
                    field='CCSD'
                    label={'CCSD (type YES for eligible or leave blank if not)'}
                    autocomplete='off'
                    //@ts-ignore
                    disabled={!formik.values.addressNotListed && formik.values.searchAddress?.districts?.find((a) => a.wardType === 'VSD')}
                  />
                </div>
              ) : null}
              {user?.votingChannel === 'mail' && (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
                <>
                  <h2 className={styles['hr']}>{t('registration.mailingAddress')}</h2>
                  <CheckBox
                    formik={formik}
                    field='differentMailingAddress'
                    checked={formik.values.differentMailingAddress}
                    onChange={() => formik.setFieldValue('differentMailingAddress', !formik.values.differentMailingAddress)}
                    label={t('registration.differentMailingAddress')}
                    ariaLabel={t('registration.differentMailingAddress')}
                    square={true}
                  />
                  {formik.values.differentMailingAddress && (
                    <MailingAddressInput
                      formik={formik}
                      styles={styles}
                      addressFieldName='mailingAddress'
                      searchAddressFieldName='searchMailingAddress'
                      addressNotListedFieldName='mailingAddressNotListed'
                      autocomplete='off'
                    />
                  )}
                </>
              )}
              {/* <div className={styles['hr']}>{t('registration.contactInformation')}</div>

              <div className={styles['form__email-section']}>
                <TextField
                  formik={formik}
                  type='email'
                  field='email'
                  placeholder={t('form.labels.email')}
                  label={t('form.labels.email')}
                  isRequired={true}
                  autocomplete='off'
                />
                <TextField
                  formik={formik}
                  type='email'
                  field='confirmEmail'
                  placeholder={t('form.labels.confirmEmail')}
                  label={t('form.labels.confirmEmail')}
                  isRequired={true}
                  autocomplete='off'
                />
              </div>
              <div className={styles['form__phone-section']}>
                <PhoneInput formik={formik} field='phone' autocomplete={'off'} />
              </div> */}

              {includeKnownIdNumber && KeycloakAdminService.isSuperAdmin() && (
                <>
                  <h2 className={styles['hr']}>{t('registration.proofOfIdentity')}</h2>
                  <div className={styles['form__license']}>
                    <TextField
                      formik={formik}
                      type='text'
                      field='knownIdNumber'
                      placeholder='00000000'
                      label={t('registration.driversLicenseIfApplicable')}
                      {...knownIdNumberTextFieldConfig}
                      autocomplete='off'
                    />
                  </div>
                </>
              )}
              <div className={styles['divider']}></div>
              {/* Buttons */}
              <div className={styles['form__actions']}>
                <NVButton text={t('buttons.save')} height={50} onClick={formik.handleSubmit} />
              </div>
              <div className={styles['privacyStatement']}>{t('registration.privacyStatement')}</div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
